// React 
import { useEffect, useRef, useState } from 'react';

// Store
import useSiteStore from '../../store/siteStore.js';
import useProfileStore from '../../store/profileStore.js';

// Components 
import { AttachFile, DeleteOutlined, EditOutlined, KeyboardArrowDownOutlined, KeyboardArrowUpOutlined, RadioButtonCheckedOutlined, AccessTime, LocalFireDepartmentOutlined, EventNoteOutlined, CommentOutlined } from '@mui/icons-material';
import {
    Card, Chip, CircularProgress, Collapse, Grid, IconButton,
    Typography, LinearProgress, Tooltip
} from '@mui/material';
import { Box } from '@mui/system';
import CustomSnackbar from './CustomSnackbar.js';

// Style
import { green, grey, red, yellow, orange } from '@mui/material/colors/index.js';
import { defaultColorScheme, tealColorScheme, darkTheme } from '../../styles/colorSchemes/index.js';
import { useAppTheme } from '../../hooks/useAppTheme.js';
import './style2.css';

// Page
import EditTrainingSession from '../dialog/EditTrainingSession.js';
import Feedback from '../dialog/Feedback.js';

// Other 
import { formatDate, formatDate2, formatTrainingFocus, getWeek } from '../../utils/index.js';
import { deleteTrainingSessionByID as deleteTSession } from '../../service/httpService/trainingSession/index.js';

const WeeklyScheduleTable = (props) => {
    const { deleteFilesAfterDownload, handleChangeTrainingSession, isAthlete, isDeletable, isEditable, isFeedbackEditable, openAndCloseTableRow, selectedAthlete } = props;
    const { feedbacks, isWeeklyScheduleLoading, openEditTrainingSession, openFeedback, removeTrainingSession, setOpenEditTrainingSession, setOpenFeedback, tableRowsOpen, setTableRowsOpen, weeklySchedule, pendingOpenTrainingSessionId, setPendingOpenTrainingSessionId }
        = useSiteStore();
    const { colorScheme } = useProfileStore();
    const { isDarkMode } = useAppTheme();

    const [selectedSession, setSelectedSession] = useState();
    const [openFail, setOpenFail] = useState(false);
    const [failMessage, setFailMessage] = useState("");
    const [openSuccess, setOpenSuccess] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [selectedFeedback, setSelectedFeedback] = useState();
    const [showFeedbackFiles, setShowFeedbackFiles] = useState(false);
    const [mapIdToIdx, setMapIdToIdx] = useState({});
    const [expandedDescriptions, setExpandedDescriptions] = useState({});

    const pendingOpenMetaRef = useRef({ id: null, startedAt: 0 });
    const clearPendingTimerRef = useRef(null);

    let colors = defaultColorScheme.trainingFocusColor;

    if (colorScheme === 'teal') {
        colors = tealColorScheme.trainingFocusColor;
    }

    const tooltipSurfaceSx = {
        bgcolor: isDarkMode ? darkTheme.cardBg : 'common.white',
        color: isDarkMode ? darkTheme.textPrimary : 'rgba(0, 0, 0, 0.87)',
        boxShadow: isDarkMode ? '0 4px 20px rgba(0, 0, 0, 0.35)' : '0 4px 20px rgba(0, 0, 0, 0.15)',
        border: `1px solid ${isDarkMode ? darkTheme.cardBorder : '#e0e0e0'}`,
    };

    useEffect(() => {
        if (weeklySchedule?.trainingSessions) {
            let dict = {};

            for (let idxTS = 0; idxTS < weeklySchedule.trainingSessions.length; idxTS++) {
                let tsID = weeklySchedule.trainingSessions[idxTS].trainingsessionid;
                dict[tsID] = "no feedback yet";

                for (let idxFB = 0; idxFB < feedbacks.length; idxFB++) {
                    if (tsID === feedbacks[idxFB].trainingsessionid) {
                        dict[tsID] = idxFB;
                    }
                }
            }
            setMapIdToIdx(dict);
        }
    }, [weeklySchedule, feedbacks]);

    useEffect(() => {
        if (!pendingOpenTrainingSessionId) return;

        if (clearPendingTimerRef.current) {
            clearTimeout(clearPendingTimerRef.current);
            clearPendingTimerRef.current = null;
        }

        // Track how long we've been trying to open this session.
        if (pendingOpenMetaRef.current.id !== pendingOpenTrainingSessionId) {
            pendingOpenMetaRef.current = { id: pendingOpenTrainingSessionId, startedAt: Date.now() };
        }

        // While switching athlete/week, the current schedule may still be for the old context.
        // Don't consume/clear the pending intent until loading finishes or a small timeout passes.
        if (isWeeklyScheduleLoading) return;
        if (!weeklySchedule?.trainingSessions || !Array.isArray(weeklySchedule.trainingSessions)) return;

        const elapsedMs = Date.now() - (pendingOpenMetaRef.current.startedAt || 0);
        const maxWaitMs = 5000;

        const sessionIndex = weeklySchedule.trainingSessions.findIndex(
            (s) => String(s?.trainingsessionid) === String(pendingOpenTrainingSessionId)
        );
        if (sessionIndex < 0) {
            if (elapsedMs >= maxWaitMs) {
                setPendingOpenTrainingSessionId(null);
            }
            return;
        }

        const trainingSession = weeklySchedule.trainingSessions[sessionIndex];
        const feedbackIndex = mapIdToIdx?.[trainingSession?.trainingsessionid];

        // Wait until the id->feedback map has been computed for this training session.
        // (During fetch, mapIdToIdx may still be from the previous schedule.)
        if (feedbackIndex === undefined) return;

        const hasFeedback = feedbackIndex !== 'no feedback yet' && feedbacks?.[feedbackIndex] !== undefined;
        if (!hasFeedback) {
            if (elapsedMs >= maxWaitMs) {
                setPendingOpenTrainingSessionId(null);
            }
            return;
        }

        const alreadyOpen =
            Array.isArray(tableRowsOpen) &&
            tableRowsOpen.length >= weeklySchedule.trainingSessions.length &&
            tableRowsOpen[sessionIndex] === true;

        // Open the collapse row (same effect as clicking the comment/paperclip chip)
        if (!alreadyOpen) {
            const nextRows = Array.isArray(tableRowsOpen) ? [...tableRowsOpen] : [];
            while (nextRows.length < weeklySchedule.trainingSessions.length) nextRows.push(false);
            nextRows[sessionIndex] = true;
            setTableRowsOpen(nextRows);
        }

        // Keep the pending intent briefly so if another effect re-initializes tableRowsOpen
        // right after navigation, we will re-open on the next render.
        clearPendingTimerRef.current = setTimeout(() => {
            setPendingOpenTrainingSessionId(null);
            clearPendingTimerRef.current = null;
        }, 350);
    }, [
        pendingOpenTrainingSessionId,
        isWeeklyScheduleLoading,
        weeklySchedule,
        feedbacks,
        mapIdToIdx,
        tableRowsOpen,
        setTableRowsOpen,
        setPendingOpenTrainingSessionId,
    ]);

    useEffect(() => {
        return () => {
            if (clearPendingTimerRef.current) {
                clearTimeout(clearPendingTimerRef.current);
                clearPendingTimerRef.current = null;
            }
        };
    }, []);
    const deleteTrainingSession = (index) => {
        const sessionId = weeklySchedule.trainingSessions[index].trainingsessionid;
        deleteTSession(localStorage.getItem('email'), sessionId)
            .then(() => {
                removeTrainingSession(sessionId);
                setSuccessMessage('Trainingseinheit erfolgreich gelöscht!');
                setOpenSuccess(true);
            })
            .catch(() => {
                setFailMessage('Löschen der Trainingseinheit fehlgeschlagen!');
                setOpenFail(true);
            });
    }

    const disabledEdit = (trainingSession) => {
        if (!weeklySchedule)
            return true;
        let sessionDate = new Date(trainingSession.date).setHours(0, 0, 0, 0) / 1000 / 60 / 60 / 24;
        let now = new Date().setHours(0, 0, 0, 0) / 1000 / 60 / 60 / 24;
        let dif = now - sessionDate;
        return weeklySchedule.calendarWeek < getWeek(new Date()) || dif > 0;
    }

    const isDeleteDisabled = (session) => {
        return (session.trainingFocus === 8 || session.trainingFocus === 7 || session.trainingFocus === 9 || session.trainingFocus === 10);
    };

    const disabledFeedback = (trainingSession) => {
        if (!weeklySchedule)
            return true;
        if (!isFeedbackEditable)
            return !trainingSessionHasFeedback(trainingSession);
        let sessionDate = new Date(trainingSession.date).setHours(0, 0, 0, 0) / 1000 / 60 / 60 / 24;
        let now = new Date().setHours(0, 0, 0, 0) / 1000 / 60 / 60 / 24;
        let dif = now - sessionDate;
        return dif < 0 || dif > 10;
    }

    const getTimeString = (time) => {
        if (!time) return;

        return time.slice(0, time.lastIndexOf(":"));
    }

    const trainingSessionHasFeedback = (trainingSession) => {
        return mapIdToIdx[trainingSession.trainingsessionid] !== undefined && mapIdToIdx[trainingSession.trainingsessionid] !== 'no feedback yet' && feedbacks[mapIdToIdx[trainingSession.trainingsessionid]] !== undefined;
    }

    //Zeigt die farbcodierte Erschöpfung des Athleten an: grün = niedrig, gelb = mittel, rot = hoch, grau = keine Angabe
    const renderExhaustion = (exhaustion) => {
        const colorMap = {
            1: green[500],
            2: yellow[500],
            3: red[500],
        };

        return (
            <RadioButtonCheckedOutlined sx={{ color: colorMap[exhaustion] || grey[500] }} />
        );
    };

    return (
        <Box>
            <CustomSnackbar
                open={openFail}
                onClose={() => setOpenFail(false)}
                message={failMessage}
                severity="error"
                autoHideDuration={4000}
            />
            <CustomSnackbar
                open={openSuccess}
                onClose={() => setOpenSuccess(false)}
                message={successMessage}
                severity="success"
                autoHideDuration={4000}
            />

            {weeklySchedule && selectedSession && (   // this prevents the column headers Tag, Fokus, Dauer, etc to move back and forth
                <>
                    <Feedback
                        deleteFilesAfterDownload={deleteFilesAfterDownload}
                        selectedFeedback={selectedFeedback}
                        setSelectedFeedback={setSelectedFeedback}
                        isFeedbackEditable={isFeedbackEditable}
                        renderExhaustion={renderExhaustion}
                        open={openFeedback}
                        onClose={() => setOpenFeedback(false)}
                        trainingSession={selectedSession}
                        athlete={selectedAthlete}
                        showFeedbackFiles={showFeedbackFiles} />

                    {isEditable && (
                        <EditTrainingSession
                            handleChangeTrainingSession={handleChangeTrainingSession}
                            athlete={selectedAthlete}
                            calendarWeek={weeklySchedule.calendarWeek}
                            setSelectedSession={setSelectedSession}
                            isAthlete={isAthlete}
                            trainingSession={selectedSession}
                            open={openEditTrainingSession}
                            onClose={() => setOpenEditTrainingSession(false)} />
                    )}
                </>
            )}

            {/* Card-based Layout */}
            <Box sx={{ p: (!weeklySchedule) && !isWeeklyScheduleLoading ? 0 : 2, position: 'relative', minHeight: (!weeklySchedule) && !isWeeklyScheduleLoading ? { xs: '400px', sm: '500px', md: '600px' } : 'auto' }}>
                {(!weeklySchedule) && !isWeeklyScheduleLoading && (
                    <Box sx={{
                        position: 'absolute',
                        top: { xs: '80px', sm: '100px', md: '120px' },
                        left: 0,
                        right: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        px: { xs: 2, sm: 4, md: 6 },
                        py: { xs: 4, sm: 5, md: 6 }
                    }}>
                        <Box sx={{
                            width: { xs: 60, sm: 70, md: 80 },
                            height: { xs: 60, sm: 70, md: 80 },
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}>
                            <EventNoteOutlined sx={{ fontSize: { xs: 48, sm: 54, md: 60 }, color: isDarkMode ? darkTheme.textSecondary : grey[400] }} />
                        </Box>
                        <Typography variant='h6' color={isDarkMode ? darkTheme.textSecondary : 'text.secondary'} fontWeight={600} mb={1} sx={{ fontSize: { xs: '1.1rem', sm: '1.15rem', md: '1.25rem' } }}>
                            Kein Wochenplan vorhanden
                        </Typography>
                        <Typography variant='body2' color={isDarkMode ? darkTheme.textSecondary : 'text.secondary'} sx={{ textAlign: 'center', maxWidth: { xs: 300, sm: 350, md: 400 }, fontSize: { xs: '0.85rem', sm: '0.875rem' } }}>
                            Für diese Woche wurde noch kein Trainingsplan erstellt.
                        </Typography>
                    </Box>
                )}

                {isWeeklyScheduleLoading && (
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        p: 4,
                        position: weeklySchedule ? 'absolute' : 'relative',
                        top: weeklySchedule ? 0 : 'auto',
                        left: 0,
                        right: 0,
                        zIndex: 10,
                        bgcolor: weeklySchedule ? (isDarkMode ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.6)') : 'transparent',
                        height: weeklySchedule ? '100%' : 'auto',
                        alignItems: weeklySchedule ? 'center' : 'flex-start'
                    }}>
                        <CircularProgress variant="indeterminate" disableShrink sx={{ color: yellow[600], animationDuration: '1000ms' }} size={50} thickness={4} />
                    </Box>
                )}

                {weeklySchedule !== "" && weeklySchedule && weeklySchedule.trainingSessions && (
                    <Grid container spacing={2}>
                        {weeklySchedule.trainingSessions.sort((a, b) => {
                            if (a.dayOfWeek < b.dayOfWeek) return -1;
                            if (a.dayOfWeek > b.dayOfWeek) return 1;
                            if (a.trainingFocus < b.trainingFocus) return -1;
                            if (a.trainingFocus > b.trainingFocus) return 1;
                            return 0;
                        }).map((trainingSession, index) => (
                            <Grid item xs={12} key={index * 11}>
                                <Card sx={{
                                    borderRadius: '12px',
                                    boxShadow: isDarkMode ? '0 4px 12px rgba(0, 0, 0, 0.4)' : '0 1px 3px rgba(0, 0, 0, 0.1)',
                                    transition: 'all 0.2s ease',
                                    borderLeft: `5px solid ${colors[trainingSession.trainingFocus - 1]}`,
                                    overflow: 'hidden',
                                    bgcolor: isDarkMode ? darkTheme.accordionBg : 'white',
                                    '&:hover': {
                                        boxShadow: isDarkMode ? '0 6px 16px rgba(0, 0, 0, 0.6)' : '0 4px 12px rgba(0, 0, 0, 0.15)',
                                        transform: 'translateX(4px)'
                                    }
                                }}>
                                    {/* Main Content Row */}
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        p: 2.5,
                                        gap: 3,
                                        bgcolor: isDarkMode ? darkTheme.accordionBg : 'white'
                                    }}>
                                        {/* Left: Category & Description */}
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.75 }}>
                                                <Typography
                                                    variant='subtitle1'
                                                    fontWeight={700}
                                                    sx={{
                                                        color: colors[trainingSession.trainingFocus - 1],
                                                        fontSize: '1.1rem'
                                                    }}
                                                >
                                                    {formatTrainingFocus(trainingSession.trainingFocus)}
                                                </Typography>
                                                <Typography variant='caption' color={isDarkMode ? darkTheme.textSecondary : 'text.secondary'} sx={{ fontSize: '0.85rem' }}>
                                                    {formatDate(trainingSession.date).split(',')[0]} {formatDate2(trainingSession.date).split(' ')[1]}
                                                </Typography>
                                            </Box>
                                            {trainingSession.description && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Typography variant='caption' color={isDarkMode ? darkTheme.textSecondary : 'text.secondary'} sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                                                        Beschreibung:
                                                    </Typography>
                                                    <Typography
                                                        variant='body2'
                                                        color='text.secondary'
                                                        sx={{
                                                            fontSize: '0.9rem',
                                                            color: isDarkMode ? darkTheme.textSecondary : grey[600]
                                                        }}
                                                    >
                                                        {expandedDescriptions[index]
                                                            ? trainingSession.description
                                                            : trainingSession.description.length > 70
                                                                ? trainingSession.description.substring(0, 70) + '...'
                                                                : trainingSession.description
                                                        }
                                                    </Typography>
                                                    {trainingSession.description.length > 70 && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => setExpandedDescriptions(prev => ({ ...prev, [index]: !prev[index] }))}
                                                            sx={{
                                                                p: 0.5,
                                                                color: colors[trainingSession.trainingFocus - 1],
                                                                flexShrink: 0
                                                            }}
                                                        >
                                                            {expandedDescriptions[index] ? <KeyboardArrowUpOutlined fontSize="small" /> : <KeyboardArrowDownOutlined fontSize="small" />}
                                                        </IconButton>
                                                    )}
                                                </Box>
                                            )}
                                        </Box>

                                        {/* Right: Stats & Actions */}
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: { xs: 1, sm: 2.5 },
                                                flexShrink: 0,
                                                flexWrap: { xs: 'wrap', sm: 'nowrap' },
                                            }}
                                        >
                                            {/* Duration - hide for focus 7, 8, 9, 10 */}
                                            {![7, 8, 9, 10].includes(trainingSession.trainingFocus) && (
                                                <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 0.75 }}>
                                                    <AccessTime sx={{ fontSize: 20, color: isDarkMode ? darkTheme.textSecondary : grey[500] }} />
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight={600}
                                                        sx={{ fontSize: '0.95rem', color: isDarkMode ? darkTheme.textPrimary : 'text.primary' }}
                                                    >
                                                        {trainingSession.duration} min
                                                    </Typography>
                                                </Box>
                                            )}

                                            {/* Intensity - hide for focus 7, 8, 9, 10 */}
                                            {![7, 8, 9, 10].includes(trainingSession.trainingFocus) && (
                                                <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 0.75, minWidth: 100 }}>
                                                    <LocalFireDepartmentOutlined sx={{ fontSize: 22, color: isDarkMode ? darkTheme.textSecondary : grey[500] }} />
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography
                                                            variant="caption"
                                                            sx={{ fontSize: '0.75rem', fontWeight: 700, color: isDarkMode ? darkTheme.textPrimary : 'text.primary' }}
                                                        >
                                                            {trainingSession.intensity}/10
                                                        </Typography>
                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={(trainingSession.intensity / 10) * 100}
                                                            sx={{
                                                                height: 5,
                                                                borderRadius: 2,
                                                                bgcolor: isDarkMode ? 'rgba(255,255,255,0.1)' : grey[200],
                                                                '& .MuiLinearProgress-bar': {
                                                                    bgcolor: colors[trainingSession.trainingFocus - 1],
                                                                },
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>
                                            )}

                                            {/* ===== FEEDBACK ICON + TOOLTIP ===== */}
                                            {trainingSessionHasFeedback(trainingSession) && (
                                                tableRowsOpen[index] ? (
                                                    <Box sx={{ display: 'inline-flex' }}>
                                                        <Chip
                                                            icon={
                                                                feedbacks[mapIdToIdx[trainingSession.trainingsessionid]].hasNewFiles
                                                                    ? <AttachFile sx={{ color: '#ff9800 !important' }} />
                                                                    : <CommentOutlined sx={{ color: '#ff9800 !important' }} />
                                                            }
                                                            label={
                                                                tableRowsOpen[index]
                                                                    ? <KeyboardArrowUpOutlined />
                                                                    : <KeyboardArrowDownOutlined />
                                                            }
                                                            onClick={() => openAndCloseTableRow(index)}
                                                            sx={{
                                                                bgcolor: 'transparent',
                                                                color: '#ff9800',
                                                                cursor: 'pointer',
                                                                '&:hover': { bgcolor: orange[50] },
                                                                '& .MuiChip-label': { px: 0.5 },
                                                            }}
                                                        />
                                                    </Box>
                                                ) : (
                                                    <Tooltip
                                                        placement="top"
                                                        slotProps={{
                                                            popper: {
                                                                modifiers: [
                                                                    {
                                                                        name: 'offset',
                                                                        options: {
                                                                            offset: [0, 10],
                                                                        },
                                                                    },
                                                                    {
                                                                        name: 'preventOverflow',
                                                                        options: {
                                                                            padding: 16,
                                                                        },
                                                                    },
                                                                ],
                                                            },
                                                            tooltip: {
                                                                sx: {
                                                                    ...tooltipSurfaceSx,
                                                                    maxWidth: 280,
                                                                    borderRadius: '10px',
                                                                    p: 0,
                                                                    overflow: 'hidden',
                                                                },
                                                            },
                                                            arrow: {
                                                                sx: {
                                                                    color: isDarkMode ? darkTheme.cardBg : 'common.white',
                                                                },
                                                            },
                                                        }}
                                                        title={
                                                            <Box sx={{ overflow: 'hidden' }}>
                                                                <Box
                                                                    sx={{
                                                                        bgcolor: colors[trainingSession.trainingFocus - 1] || (isDarkMode ? darkTheme.accent : '#1976d2'),
                                                                        px: 1.5,
                                                                        py: 0.75,
                                                                        textAlign: 'center',
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        variant="subtitle2"
                                                                        fontWeight={700}
                                                                        sx={{
                                                                            color: isDarkMode ? 'rgba(0, 0, 0, 0.87)' : 'white',
                                                                            fontSize: '0.82rem',
                                                                        }}
                                                                    >
                                                                        Rückmeldung
                                                                    </Typography>
                                                                </Box>

                                                                <Box sx={{ px: 1.5, py: 1, minWidth: '190px' }}>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                                            <AccessTime sx={{ fontSize: 15, color: isDarkMode ? darkTheme.textSecondary : '#666' }} />
                                                                            <Typography variant="caption" sx={{ color: isDarkMode ? darkTheme.textSecondary : '#666', fontSize: '0.75rem' }}>
                                                                                Dauer
                                                                            </Typography>
                                                                        </Box>
                                                                        <Typography variant="caption" fontWeight={700} sx={{ color: isDarkMode ? darkTheme.textPrimary : 'rgba(0, 0, 0, 0.87)', fontSize: '0.75rem' }}>
                                                                            {feedbacks[mapIdToIdx[trainingSession.trainingsessionid]].actualDuration} min
                                                                        </Typography>
                                                                    </Box>

                                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                                            <LocalFireDepartmentOutlined sx={{ fontSize: 15, color: isDarkMode ? darkTheme.textSecondary : '#666' }} />
                                                                            <Typography variant="caption" sx={{ color: isDarkMode ? darkTheme.textSecondary : '#666', fontSize: '0.75rem' }}>
                                                                                Intensität
                                                                            </Typography>
                                                                        </Box>
                                                                        <Typography variant="caption" fontWeight={700} sx={{ color: isDarkMode ? darkTheme.textPrimary : 'rgba(0, 0, 0, 0.87)', fontSize: '0.75rem' }}>
                                                                            {feedbacks[mapIdToIdx[trainingSession.trainingsessionid]].actualIntensity}/10
                                                                        </Typography>
                                                                    </Box>

                                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                                            <RadioButtonCheckedOutlined sx={{ fontSize: 15, color: isDarkMode ? darkTheme.textSecondary : '#666' }} />
                                                                            <Typography variant="caption" sx={{ color: isDarkMode ? darkTheme.textSecondary : '#666', fontSize: '0.75rem' }}>
                                                                                Erschöpfung
                                                                            </Typography>
                                                                        </Box>
                                                                        <Box sx={{ display: 'flex', gap: 0.4 }}>
                                                                            {renderExhaustion(
                                                                                feedbacks[mapIdToIdx[trainingSession.trainingsessionid]].physicalExhaustion
                                                                            )}
                                                                            {renderExhaustion(
                                                                                feedbacks[mapIdToIdx[trainingSession.trainingsessionid]].mentalExhaustion
                                                                            )}
                                                                        </Box>
                                                                    </Box>

                                                                    {feedbacks[mapIdToIdx[trainingSession.trainingsessionid]].hasNewFiles && (
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 0.5 }}>
                                                                            <AttachFile sx={{ fontSize: 14, color: '#ff9800' }} />
                                                                            <Typography variant="caption" sx={{ color: '#ff9800', fontSize: '0.72rem' }}>
                                                                                Anhang vorhanden
                                                                            </Typography>
                                                                        </Box>
                                                                    )}

                                                                    <Box sx={{ pt: 0.3, borderTop: `1px dashed ${isDarkMode ? darkTheme.cardBorder : '#e0e0e0'}` }}>
                                                                        <Typography variant="caption" sx={{ display: 'block', color: isDarkMode ? darkTheme.textPrimary : 'rgba(0, 0, 0, 0.75)', fontSize: '0.7rem', fontWeight: 600 }}>
                                                                            Klicken Sie, um den Kommentar zu sehen.
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                            </Box>
                                                        }
                                                    >
                                                        <Box sx={{ display: 'inline-flex' }}>
                                                            <Chip
                                                                icon={
                                                                    feedbacks[mapIdToIdx[trainingSession.trainingsessionid]].hasNewFiles
                                                                        ? <AttachFile sx={{ color: '#ff9800 !important' }} />
                                                                        : <CommentOutlined sx={{ color: '#ff9800 !important' }} />
                                                                }
                                                                label={
                                                                    tableRowsOpen[index]
                                                                        ? <KeyboardArrowUpOutlined />
                                                                        : <KeyboardArrowDownOutlined />
                                                                }
                                                                onClick={() => openAndCloseTableRow(index)}
                                                                sx={{
                                                                    bgcolor: 'transparent',
                                                                    color: '#ff9800',
                                                                    cursor: 'pointer',
                                                                    '&:hover': { bgcolor: orange[50] },
                                                                    '& .MuiChip-label': { px: 0.5 },
                                                                }}
                                                            />
                                                        </Box>
                                                    </Tooltip>
                                                )
                                            )}

                                            {/* Action Buttons */}
                                            {isEditable && !trainingSessionHasFeedback(trainingSession) && (
                                                trainingSession.trainingFocus === 10 && !isAthlete ? (
                                                    <Tooltip
                                                        title={disabledEdit(trainingSession) ? '' : 'Trainingseinheit löschen'}
                                                        disableHoverListener={disabledEdit(trainingSession)}
                                                        disableFocusListener={disabledEdit(trainingSession)}
                                                        disableTouchListener={disabledEdit(trainingSession)}
                                                        placement="top"
                                                    >
                                                        <Box sx={{ display: 'inline-flex' }}>
                                                            <IconButton
                                                                disabled={disabledEdit(trainingSession)}
                                                                onClick={() => deleteTrainingSession(weeklySchedule.trainingSessions.findIndex(s => s.trainingsessionid === trainingSession.trainingsessionid))}
                                                                size="small"
                                                                sx={{
                                                                    color: isDarkMode ? red[400] : red[600],
                                                                    '&:hover': {
                                                                        bgcolor: isDarkMode ? 'rgba(244, 67, 54, 0.12)' : 'rgba(211, 47, 47, 0.08)'
                                                                    },
                                                                    '&:disabled': {
                                                                        color: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : grey[300]
                                                                    }
                                                                }}
                                                            >
                                                                <DeleteOutlined fontSize="small" />
                                                            </IconButton>
                                                        </Box>
                                                    </Tooltip>
                                                ) : (
                                                    <Tooltip
                                                        title={disabledEdit(trainingSession) ? '' : 'Trainingseinheit bearbeiten'}
                                                        disableHoverListener={disabledEdit(trainingSession)}
                                                        disableFocusListener={disabledEdit(trainingSession)}
                                                        disableTouchListener={disabledEdit(trainingSession)}
                                                        placement="top"
                                                    >
                                                        <Box sx={{ display: 'inline-flex' }}>
                                                            <IconButton
                                                                disabled={disabledEdit(trainingSession)}
                                                                onClick={() => {
                                                                    setOpenEditTrainingSession(true);
                                                                    setOpenFeedback(false);
                                                                    setSelectedSession(trainingSession);
                                                                }}
                                                                size="small"
                                                                sx={{
                                                                    color: isDarkMode ? orange[500] : 'primary.main',
                                                                    '&:hover': {
                                                                        bgcolor: isDarkMode ? 'rgba(255, 152, 0, 0.1)' : 'rgba(25, 118, 210, 0.08)'
                                                                    },
                                                                    '&:disabled': {
                                                                        color: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : grey[300]
                                                                    }
                                                                }}
                                                            >
                                                                <EditOutlined fontSize="small" />
                                                            </IconButton>
                                                        </Box>
                                                    </Tooltip>
                                                )
                                            )}

                                            {!isEditable && isFeedbackEditable && (
                                                <Tooltip
                                                    title={disabledFeedback(trainingSession) ? '' : 'Rückmeldung bearbeiten'}
                                                    disableHoverListener={disabledFeedback(trainingSession)}
                                                    disableFocusListener={disabledFeedback(trainingSession)}
                                                    disableTouchListener={disabledFeedback(trainingSession)}
                                                    placement="top"
                                                >
                                                    <Box sx={{ display: 'inline-flex' }}>
                                                        <IconButton
                                                            disabled={disabledFeedback(trainingSession)}
                                                            onClick={() => {
                                                                setOpenFeedback(true);
                                                                setSelectedSession(trainingSession);
                                                                setSelectedFeedback(feedbacks[mapIdToIdx[trainingSession.trainingsessionid]]);
                                                                setShowFeedbackFiles(false);
                                                            }}
                                                            size="small"
                                                            sx={{
                                                                color: isDarkMode ? orange[500] : 'primary.main',
                                                                '&:hover': {
                                                                    bgcolor: isDarkMode ? 'rgba(255, 152, 0, 0.1)' : 'rgba(25, 118, 210, 0.08)'
                                                                },
                                                                '&:disabled': {
                                                                    color: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : grey[300]
                                                                }
                                                            }}
                                                        >
                                                            <EditOutlined fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                </Tooltip>
                                            )}

                                            {isAthlete && isDeletable && (
                                                <IconButton
                                                    onClick={() => deleteTrainingSession(index)}
                                                    disabled={!isDeleteDisabled(trainingSession)}
                                                    size="small"
                                                    sx={{
                                                        color: red[500],
                                                        '&:hover': {
                                                            bgcolor: red[50]
                                                        },
                                                        '&:disabled': {
                                                            color: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : grey[300]
                                                        }
                                                    }}
                                                >
                                                    <DeleteOutlined fontSize="small" />
                                                </IconButton>
                                            )}
                                        </Box>
                                    </Box>

                                    {/* Collapsible Feedback Section */}
                                    <Collapse in={tableRowsOpen[index]} timeout="auto" unmountOnExit>
                                        {trainingSessionHasFeedback(trainingSession) && selectedAthlete !== null && selectedAthlete !== undefined && (
                                            <Box sx={{
                                                p: 2,
                                                pl: 4,
                                                ml: 2,
                                                mr: 2,
                                                mb: 1,
                                                bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.03)' : grey[50],
                                                borderLeft: `3px solid ${colors[trainingSession.trainingFocus - 1]}`,
                                                borderRadius: '0 0 8px 8px'
                                            }}>
                                                {/* Header */}
                                                <Box sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    mb: 2
                                                }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                        <Typography variant='subtitle2' fontWeight={700} color={green[700]}>
                                                            Rückmeldung
                                                        </Typography>
                                                        {trainingSessionHasFeedback(trainingSession) && feedbacks[mapIdToIdx[trainingSession.trainingsessionid]].lastEdited !== null && (
                                                            <Typography variant='caption' color={isDarkMode ? darkTheme.textSecondary : 'text.secondary'}>
                                                                {formatDate2(feedbacks[mapIdToIdx[trainingSession.trainingsessionid]].lastEdited)}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                    {trainingSessionHasFeedback(trainingSession) && feedbacks[mapIdToIdx[trainingSession.trainingsessionid]].hasNewFiles && (
                                                        <Tooltip
                                                            title="Dateianhänge anzeigen"
                                                            placement="top"
                                                        >

                                                            <IconButton
                                                                onClick={() => {
                                                                    setOpenFeedback(true);
                                                                    setSelectedSession(trainingSession);
                                                                    setSelectedFeedback(feedbacks[mapIdToIdx[trainingSession.trainingsessionid]]);
                                                                    setShowFeedbackFiles(true);
                                                                }}
                                                                size="small"
                                                                sx={{
                                                                    color: '#ff9800',
                                                                    '&:hover': {
                                                                        bgcolor: 'rgba(255, 152, 0, 0.1)'
                                                                    }
                                                                }}>
                                                                <AttachFile fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Box>

                                                {/* Stats Row */}
                                                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                                                    {/* Duration */}
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <AccessTime sx={{ fontSize: 18, color: isDarkMode ? darkTheme.textSecondary : grey[500] }} />
                                                        <Typography variant='body2' fontWeight={600} color={isDarkMode ? darkTheme.textPrimary : 'text.primary'}>
                                                            {trainingSessionHasFeedback(trainingSession) && feedbacks[mapIdToIdx[trainingSession.trainingsessionid]].actualDuration} min
                                                        </Typography>
                                                    </Box>

                                                    {/* Intensity */}
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <LocalFireDepartmentOutlined sx={{ fontSize: 20, color: isDarkMode ? darkTheme.textSecondary : grey[500] }} />
                                                        <Typography variant='body2' fontWeight={700} color={isDarkMode ? darkTheme.textPrimary : 'text.primary'}>
                                                            {trainingSessionHasFeedback(trainingSession) && feedbacks[mapIdToIdx[trainingSession.trainingsessionid]].actualIntensity}/10
                                                        </Typography>
                                                    </Box>

                                                    {/* Exhaustion */}
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <Typography variant='body2' color={isDarkMode ? darkTheme.textSecondary : 'text.secondary'} sx={{ mr: 0.5 }}>
                                                            Erschöpfung:
                                                        </Typography>
                                                        {trainingSessionHasFeedback(trainingSession) && (
                                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                                {renderExhaustion(feedbacks[mapIdToIdx[trainingSession.trainingsessionid]].physicalExhaustion)}
                                                                {renderExhaustion(feedbacks[mapIdToIdx[trainingSession.trainingsessionid]].mentalExhaustion)}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </Box>

                                                {/* Comment */}
                                                {trainingSessionHasFeedback(trainingSession) && feedbacks[mapIdToIdx[trainingSession.trainingsessionid]].comment && (
                                                    <Box sx={{ mb: 2 }}>
                                                        <Typography variant='caption' color={isDarkMode ? darkTheme.textSecondary : 'text.secondary'} fontWeight={600} display='block' mb={0.5}>
                                                            Kommentar
                                                        </Typography>
                                                        <Typography variant='body2' color={isDarkMode ? darkTheme.textPrimary : 'text.primary'} sx={{ lineHeight: 1.6 }}>
                                                            {feedbacks[mapIdToIdx[trainingSession.trainingsessionid]].comment}
                                                        </Typography>
                                                    </Box>
                                                )}

                                                {/* Extended Feedback */}
                                                {trainingSessionHasFeedback(trainingSession) && selectedAthlete.hasExtendedFeedback && feedbacks[mapIdToIdx[trainingSession.trainingsessionid]].extendedFeedback && (
                                                    <Box sx={{
                                                        display: 'flex',
                                                        gap: 3,
                                                        flexWrap: 'wrap',
                                                        pt: 1,
                                                        borderTop: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : grey[200]}`
                                                    }}>
                                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                            <Typography variant='caption' color={isDarkMode ? darkTheme.textSecondary : 'text.secondary'}>Weckzeit:</Typography>
                                                            <Typography variant='caption' fontWeight={600} color={isDarkMode ? darkTheme.textPrimary : 'text.primary'}>
                                                                {getTimeString(feedbacks[mapIdToIdx[trainingSession.trainingsessionid]].extendedFeedback.wakeUpTime, trainingSession)}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                            <Typography variant='caption' color={isDarkMode ? darkTheme.textSecondary : 'text.secondary'}>Schlafdauer:</Typography>
                                                            <Typography variant='caption' fontWeight={600} color={isDarkMode ? darkTheme.textPrimary : 'text.primary'}>
                                                                {feedbacks[mapIdToIdx[trainingSession.trainingsessionid]].extendedFeedback.sleepDuration} h
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                            <Typography variant='caption' color={isDarkMode ? darkTheme.textSecondary : 'text.secondary'}>Ruhepuls:</Typography>
                                                            <Typography variant='caption' fontWeight={600} color={isDarkMode ? darkTheme.textPrimary : 'text.primary'}>
                                                                {feedbacks[mapIdToIdx[trainingSession.trainingsessionid]].extendedFeedback.restingPulse} bpm
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                            <Typography variant='caption' color={isDarkMode ? darkTheme.textSecondary : 'text.secondary'}>Gewicht:</Typography>
                                                            <Typography variant='caption' fontWeight={600} color={isDarkMode ? darkTheme.textPrimary : 'text.primary'}>
                                                                {feedbacks[mapIdToIdx[trainingSession.trainingsessionid]].extendedFeedback.weight} kg
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                )}
                                            </Box>
                                        )}
                                    </Collapse>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Box>
        </Box >
    )
}

export default WeeklyScheduleTable;
